import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, PlusIcon, TagIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Service, ServiceSku, useCreateSku, useUpdateSku } from '@/api/services'
import { CurrencyInputFormField } from '@/components/form/currency-input-form-field'
import { NumericInputFormField } from '@/components/form/numeric-input-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { Grid } from '@/components/ui/grid'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { Typography } from '@/components/ui/typography'
import { toastFactory } from '@/factories'
import { useDirtyFormGuard, useFormSave } from '@/hooks'
import { getApiErrorMessage } from '@/utils/error'

import { FormChangeFields, getFormChanges } from '../../shared/form-changes'
import { PersonFormLayout } from '../../shared/person-form-layout'
import { SwitchRow } from '../../shared/switch-row'
import { UpdatedAt } from '../../shared/updated-at'
import {
  SKU_COST_MAX,
  SKU_DURATION_MAX,
  SKU_NAME_MAX_LENGTH,
  SKU_QUANTITY_MAX,
} from '../services.constants'
import { getTrainingTypeStatus, isTimedCategory } from '../services.utils'
import { getSkuFormDefaults, makeSkuFormSchema, SkuFormData } from './sku-form-modal.constants'

interface SkuFormModalProps {
  duplicate?: boolean
  service: Service
  sku?: ServiceSku
  onClose: VoidFunction
  onSaved?: (id: number) => void
}

export const SkuFormModal = ({
  duplicate = false,
  service,
  sku,
  onClose,
  onSaved,
}: SkuFormModalProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const { mutateAsync: createSku } = useCreateSku()
  const { mutateAsync: updateSku } = useUpdateSku()

  const isEdit = !!sku && !duplicate
  const timed = isTimedCategory(service.category)

  const schema = useMemo(() => makeSkuFormSchema(t, { timed }), [t, timed])

  const form = useForm<SkuFormData>({
    defaultValues: getSkuFormDefaults(sku),
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })

  const { isDirty, isSubmitting } = form.formState
  const duration = form.watch('duration')
  const trainingType = timed ? getTrainingTypeStatus(service, duration || null) : null

  const changeFields = useMemo<FormChangeFields<SkuFormData>>(
    () => ({
      active: {
        format: (value) => t(value ? 'common:booleans.yes' : 'common:booleans.no'),
        label: t('organisation:servicesPage.sku.fields.active'),
      },
      cost: { label: t('organisation:servicesPage.sku.fields.cost.label') },
      duration: { label: t('organisation:servicesPage.sku.fields.duration.label') },
      name: { label: t('organisation:servicesPage.sku.fields.name.label') },
      quantity: { label: t('organisation:servicesPage.sku.fields.quantity.label') },
    }),
    [t],
  )

  const guard = useDirtyFormGuard(
    isDirty && !isSubmitting,
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    { inPopup: true },
  )

  const handleClose = () => {
    guard(onClose)
    return false
  }

  const onSubmit: SubmitHandler<SkuFormData> = async (data) => {
    const fields = {
      cost: Number(data.cost),
      duration: timed && data.duration ? Number(data.duration) : undefined,
      name: data.name.trim(),
      productId: service.id,
      quantity: timed && data.quantity ? Number(data.quantity) : undefined,
    }

    try {
      if (isEdit && sku) {
        await updateSku({ ...fields, active: data.active, id: sku.id })
        toastFactory.success(t('organisation:servicesPage.sku.toasts.updated'))
      } else {
        const created = await createSku(fields)
        toastFactory.success(
          t(
            duplicate
              ? 'organisation:servicesPage.sku.toasts.duplicated'
              : 'organisation:servicesPage.sku.toasts.created',
          ),
        )
        onSaved?.(created.id)
      }
      onClose()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const title = duplicate
    ? t('organisation:servicesPage.sku.modal.titleDuplicate')
    : isEdit
      ? t('organisation:servicesPage.sku.modal.titleEdit')
      : t('organisation:servicesPage.sku.modal.titleNew')

  const submit = useFormSave({ form, isEdit, onSubmit, onUnchanged: onClose })

  return (
    <Modal
      autoWidth
      title={title}
      headerIcon={TagIcon}
      footer={
        <>
          {isEdit && <UpdatedAt value={sku?.updatedAt} />}
          <Button use="secondary" onClick={handleClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button loading={isSubmitting} use="primary" onClick={submit}>
            {isEdit ? t('common:actions.save') : t('common:actions.add')}
            <Icon icon={isEdit ? CheckIcon : PlusIcon} />
          </Button>
        </>
      }
      onBeforeClose={handleClose}
      onClose={onClose}
    >
      <PersonFormLayout form={form} onSubmit={submit}>
        <TextInputFormField
          withReservedFooter
          required
          label={t('organisation:servicesPage.sku.fields.name.label')}
          maxLength={SKU_NAME_MAX_LENGTH}
          name="name"
          placeholder={t('organisation:servicesPage.sku.fields.name.placeholder')}
        />

        {timed && (
          <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
            <NumericInputFormField
              withReservedFooter
              required
              label={t('organisation:servicesPage.sku.fields.duration.label')}
              max={SKU_DURATION_MAX}
              mode="integer"
              name="duration"
              placeholder={t('organisation:servicesPage.sku.fields.duration.placeholder')}
            />
            <NumericInputFormField
              withReservedFooter
              label={t('organisation:servicesPage.sku.fields.quantity.label')}
              max={SKU_QUANTITY_MAX}
              mode="integer"
              name="quantity"
              placeholder={t('organisation:servicesPage.sku.fields.quantity.placeholder')}
            />
          </Grid>
        )}

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <div />
          <CurrencyInputFormField
            withReservedFooter
            required
            label={t('organisation:servicesPage.sku.fields.cost.label')}
            max={SKU_COST_MAX}
            name="cost"
            placeholder={t('organisation:servicesPage.sku.fields.cost.placeholder')}
          />
        </Grid>

        {trainingType && !trainingType.created && trainingType.reason === 'missingVisuals' && (
          <Typography color="red500" size="text-small">
            {t('organisation:servicesPage.sku.classType.missingVisuals')}
          </Typography>
        )}

        <SwitchRow label={t('organisation:servicesPage.sku.fields.active')} name="active" />
      </PersonFormLayout>
    </Modal>
  )
}
